import React, { useEffect, useState, useContext } from "react";
import * as d3 from "d3";
// import { useCookies } from "react-cookie";
import WorkflowContext from "../workflowContext";
import fetchOptionsBuilder from "../fetchOptionBuilder";
import CategoryFilterMenu from "../components/CategoryFilterMenu";
import '../styles/pages/forcetree.scss'
import { Checkbox } from "primereact/checkbox";


function getSessionStorageOrDefault(key, defaultValue) {
    const stored =  sessionStorage.getItem(key);
    if (!stored) {
      return defaultValue;
    }
    return JSON.parse(stored);

  }

const Forcetree = () => {
    const workflow = useContext(WorkflowContext);
    // const [cookies, setCookie, removeCookie] = useCookies(['auth', 'userid']);
    const [data, setData] = useState(null);
    const [metadata, setMetadata] = useState({});
    const [selectedProperty, setSelectedProperty] = useState('');
    const [textFilter, setTextFilter] = useState(/.+/g);
    const [propertyMap, setPropertyMap] = useState(null);
    const [shownValues, setShownValues] = useState(null);
    const [stopNodes, setStopNodes] = useState(new Set());
    const [zoomTransform, setZoomTransform] = useState(d3.zoomIdentity);

    const colorScheme = [];
    if (selectedProperty) {
        const propLength = Object.keys(propertyMap[selectedProperty]).length;
        for (let i = 0; i < propLength; i++) {
            colorScheme.push(d3.interpolateRainbow(i / propLength));
        }
    }
    const color = d3.scaleOrdinal(colorScheme);

    const [apitoken, setApitoken] = useState(             // eslint-disable-line no-unused-vars
        getSessionStorageOrDefault('apitoken', null)
    );

    const getDataUserId = JSON.parse(sessionStorage.getItem('userid'));

    useEffect(() => {
        document.title = `Forcetree | Palmona Pathogenomics`
    }, []);

    useEffect(() => {
        fetch(process.env.REACT_APP_VISUALIZATION_API_PATH + `clusters/full_tree/forcetree`, fetchOptionsBuilder(apitoken, workflow, getDataUserId))
            .then(res => res.json())
            .then(val => setData(val))
            .catch(err => {
                console.error(err);
            })
    }, [workflow]);

    useEffect(() => {
        fetch(process.env.REACT_APP_VISUALIZATION_API_PATH + 'metadata/json', fetchOptionsBuilder(apitoken, workflow, getDataUserId))
            .then(res => res.json())
            .then(val => setMetadata(val))
            .catch(err => {
                console.error(err);
            })
    }, [workflow]);

    // create category map
    useEffect(() => {
        if (data && metadata) {
            const propOptions = Object.keys(metadata);
            setSelectedProperty(propOptions[0]);
            const newPropertyMap = {};
            propOptions.forEach(property => {
                newPropertyMap[property] = {};
                Object.values(metadata[property]).forEach(val => {
                    if (!newPropertyMap[property][val]) newPropertyMap[property][val] = 0;
                    newPropertyMap[property][val]++;
                });
            });
            setPropertyMap(newPropertyMap);
        }
    }, [data, metadata]);

    // set the shown values on data load/selected property change
    useEffect(() => {
        if (propertyMap && selectedProperty) {
            const newShownValues = {};
            for (const val in propertyMap[selectedProperty]) {
                newShownValues[val] = true;
            }
            setShownValues(newShownValues);
        }

    }, [data, selectedProperty, propertyMap])

    const drag = simulation => {
  
        function dragstarted(event, d) {
          if (!event.active) simulation.alphaTarget(0.3).restart();
          d.fx = d.x;
          d.fy = d.y;
        }
        
        function dragged(event, d) {
          d.fx = event.x;
          d.fy = event.y;
        }
        
        function dragended(event, d) {
          if (!event.active) simulation.alphaTarget(0);
          d.fx = null;
          d.fy = null;
        }
        
        return d3.drag()
            .on("start", dragstarted)
            .on("drag", dragged)
            .on("end", dragended);
    }

    useEffect(() => {
        if (data && selectedProperty && propertyMap && shownValues && metadata && selectedProperty in metadata) {
            const width = window.innerWidth + 100;
            const height = window.innerHeight + 100;
            d3.select("#forcetree-graph svg").remove();
            d3.select("#forcetree-graph div").remove();
            const svg = d3.create("svg")
                .attr("width", width)
                .attr("height", height)
                .attr("viewBox", [-width / 2, -height / 2, width, height])
                .attr("style", "max-width: 100%; height: auto;");


            const hiddenNodes = new Set();
            const filterNode = (node, hidden) => {
                if (!node.children) {
                    if (hidden) hiddenNodes.add(node.name);
                    if (!node.name.match(textFilter)) hiddenNodes.add(node.name);
                    if (!shownValues[metadata[selectedProperty][node.name]]) hiddenNodes.add(node.name);
                } else {
                    let hideThisNode = stopNodes.has(node.name) ? hidden : true;
                    let hideChildren = hidden;
                    if (stopNodes.has(node.name)) {
                        hideChildren = true;
                    }
                    node.children.forEach(child => {
                        filterNode(child, hideChildren);
                        if (!hiddenNodes.has(child.name)) hideThisNode = false;
                    });
                    if (hideThisNode) hiddenNodes.add(node.name);
                }
            }

            try {
                filterNode(data);
            } catch (err) {
                console.error(err)
            }
            
            
            const root = d3.hierarchy(data);
            const links = root.links();
            const nodes = root.descendants();
          
            const simulation = d3.forceSimulation(nodes)
                .force("link", d3.forceLink(links).id(d => d.id).distance(d => d.target.data.value / 15).strength(2))
                .force("charge", d3.forceManyBody().strength(-50))
                // .force("x", d3.forceX())
                // .force("y", d3.forceY());
          
            // advance simulation to displayed tree
            simulation.tick(300);

            const link = svg.append("g")
                .attr("stroke", "#999")
                .attr("stroke-opacity", 0.6)
                .selectAll("line")
                .data(links)
                .join("line")
                .attr("class", d => `link-${d.source.data.name}`)
                .style("opacity", d => hiddenNodes.has(d.target.data.name) ? "0" : "1")
          
            const node = svg.append("g")
                .attr("stroke-width", 1.5)
                .selectAll("circle")
                .data(nodes)
                .join("circle")
                .attr("id", d => d.data.name)
                .attr("r", d => d.data.children ? 2.5 : 5)
                .attr("stroke", d => stopNodes.has(d.data.name) ? "#000" : "#fff")
                .attr("class", "node-circle")
                .attr("fill", d => d.data.children ? stopNodes.has(d.data.name) ? "#fff" : "#000" : color(metadata[selectedProperty][d.data.name]))
                .style("opacity", d => hiddenNodes.has(d.data.name) ? "0" : "1")
                // .call(drag(simulation));
          
            node.append("title")
                .text(d => d.data.name);
          
            simulation.on("tick", () => {
              link
                  .attr("x1", d => d.source.x)
                  .attr("y1", d => d.source.y)
                  .attr("x2", d => d.target.x)
                  .attr("y2", d => d.target.y);
          
              node
                  .attr("cx", d => d.x)
                  .attr("cy", d => d.y);
            });

            document.getElementById('forcetree-graph').append(svg.node())

            const tooltip = d3.select('#forcetree-graph')
                .append('div')
                .style("opacity", 0)
                .attr("class", "tooltip")
                .style("backgroud-color", "white")
                .style("border", "solid")
                .style("padding", "5px")
                .style("width", "100px")
                .style("z-index", "10")
                .style("background-color", "white")
                .style("font-size", "12px")
                .style("position", "absolute")

            const mouseoverNode = e => {
                const nodeName = e.target.__data__.data.name
                d3.select(`#${nodeName}`)
                    .attr('r', 7)
                if (!e.target.__data__.data.children) {
                    let tooltipText = `<b>Genome ID: ${nodeName}</b><br>`;
                    tooltipText += `<b>${selectedProperty}: ${metadata[selectedProperty][nodeName]}</b><br>`
                    for (const property of Object.keys(metadata)) {
                        if (property !== selectedProperty) {
                            tooltipText += `${property}: ${metadata[property][nodeName]}<br>`
                        }
                    }
                    tooltip
                        .style("opacity", 1)
                        .style("left", (e.pageX - 250) + "px")
                        .style("top", (e.pageY - 50) + "px")
                        .html(tooltipText)
                }
            }

            const mouseoutNode = e => {
                const nodeName = e.target.__data__.data.name;
                d3.select(`#${nodeName}`)
                    .attr('r', d => d.data.children ? 2.5 : 5)
                tooltip
                    .style("opacity", 0)
                    .html('')
            }

            const mouseclickNode = e => {
                const node = e.target.__data__
                if (node.data.children) {
                    const newStopNodes = new Set(stopNodes);
                    if (stopNodes.has(node.data.name)) {
                        newStopNodes.delete(node.data.name);
                    } else {
                        newStopNodes.add(node.data.name);
                    }
                    setStopNodes(newStopNodes);
                }
            }

            node
                .on("mouseover", e => mouseoverNode(e))
                .on("mouseout", e => mouseoutNode(e))
                .on("click", e => mouseclickNode(e))

            // add zoom behavior
            const handleZoom = e => {
                setZoomTransform(e.transform);
                d3.selectAll("g")
                    .attr('transform', e.transform);
            }

            let zoom = d3.zoom()
                // .translateExtent([[-width, -height], [width, height]])
                .on('zoom', handleZoom);

            svg.call(zoom);
            // apply initial zoom or stateful transform value on reload
            svg.call(zoom.transform, zoomTransform);
          
            // invalidation.then(() => simulation.stop());
          
        }
    }, [data, textFilter, selectedProperty, metadata, shownValues, stopNodes])

    const changeShownValue = (value, checked) => {
        const newShownValues = {...shownValues};
        newShownValues[value] = checked;
        setShownValues(newShownValues);
    }
    return (
        <div id='forcetree-container'>
            <div id='side-display'>
                <h3>Force-directed Graph</h3>
                <div className="w-14rem">
                    <CategoryFilterMenu propOptions = {Object.keys(metadata)} selectedProperty={selectedProperty} setSelectedProperty={setSelectedProperty} setTextFilter={setTextFilter}/>
                </div>
            </div>
            <div id='forcetree-graph'></div>
            <div style={{marginTop:"2rem"}} className="flex-item menu" id="category-filter-menu">
                {propertyMap && selectedProperty &&
                    <div>
                    <span style={{ fontWeight: "bold", marginBottom: "1rem" }}>{selectedProperty}</span>
                    {Object.entries(propertyMap[selectedProperty]).map(([value, count]) => (
                        <div>
                        {shownValues && <Checkbox checked={shownValues[value]} onChange={e => changeShownValue(value, e.checked)}/>}
                        <svg width="20" height="20">
                            <circle cx="10" cy="10" r="8" fill={color(value)}></circle>
                        </svg>
                        <span style={{ color: color(value) }}>{value}</span>:{count}
                        </div>
                    ))}
                    </div>
              }
          </div>
        </div>
        
    )
    
}

export default Forcetree;